import React, { useId, useState } from "react";
import { FILTERS } from "./state";
import { EVIDENCE_LABEL, SACRED_LABEL, STATUS_LABEL } from "./labels";

// Every facet is counted over the UNFILTERED set, so the numbers do not move
// underneath a reader as they click. A facet with one option is not offered:
// a control that can only say what the page already says is noise.
const GROUPS = [
  { name: "status", legend: "Evidence status", label: (key) => STATUS_LABEL[key] || key },
  { name: "sacred", legend: "Ancient sources", label: (key) => SACRED_LABEL[key] || key },
  { name: "evidence", legend: "Kind of evidence", label: (key) => EVIDENCE_LABEL[key] || key },
  { name: "kind", legend: "Relation", label: (key) => key },
  { name: "school", legend: "Who says so", label: (key, schools) => schools[key] || key }
];

export default function FilterBar({ facets, filters, onChange, schools, shown, total }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const active = FILTERS.filter((name) => filters[name]);
  const offered = GROUPS.filter(({ name }) => Object.keys(facets[name] || {}).length > 1);

  if (offered.length === 0) return null;

  return (
    <div className="correspondence-filters">
      <div className="correspondence-filters__bar">
        <button
          type="button"
          className="correspondence-filters__toggle"
          aria-expanded={open}
          aria-controls={panelId}
          data-testid="correspondence-filter-toggle"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          Narrow these
          <span className="correspondence-filters__count" data-testid="correspondence-filter-count">
            {active.length ? `${active.length} on` : "all shown"}
          </span>
        </button>

        <p className="correspondence-filters__reading" aria-live="polite" data-testid="correspondence-shown">
          {shown === total
            ? `${total} correspondence${total === 1 ? "" : "s"}`
            : `${shown} of ${total} shown`}
        </p>

        {/* Stays mounted and goes disabled: a control that vanishes when you
            use it throws focus to the top of the document. */}
        <button
          type="button"
          className="correspondence-filters__clear"
          disabled={active.length === 0}
          data-testid="correspondence-filter-clear"
          onClick={() => onChange({})}
        >
          Show every correspondence
        </button>
      </div>

      {/* Kept mounted rather than unmounted on close: these controls hold the
          reader's narrowing, and discarding it on collapse would lose it. */}
      <div className="correspondence-filters__panel" id={panelId} hidden={!open}>
        {offered.map(({ name, legend, label }) => (
          <fieldset key={name} className="correspondence-filters__group">
            <legend>{legend}</legend>
            {Object.entries(facets[name]).map(([value, count]) => (
              <label key={value} className="correspondence-filters__option">
                <input
                  type="radio"
                  name={name}
                  checked={filters[name] === value}
                  data-testid={`correspondence-filter-${name}-${value}`}
                  onChange={() => onChange({ ...filters, [name]: value })}
                />
                <span>
                  {label(value, schools)} <span className="correspondence-filters__tally">{count}</span>
                </span>
              </label>
            ))}
            <label className="correspondence-filters__option">
              <input
                type="radio"
                name={name}
                checked={!filters[name]}
                data-testid={`correspondence-filter-${name}-any`}
                onChange={() => {
                  const next = { ...filters };
                  delete next[name];
                  onChange(next);
                }}
              />
              <span>Any</span>
            </label>
          </fieldset>
        ))}
      </div>
    </div>
  );
}
